import { Injectable, Inject } from '@angular/core';
import { Http } from '@angular/http';
import { Observable } from 'rxjs/Observable';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';

import { Configuration } from '../configurations/shared.config';
import { LookUpTableName, IErrorHandler, IErrorHandlerToken, DBSchema, WriteLookup, FilterSearchCriteria } from '../dataModels';

const api = Configuration.urls;

@Injectable()
export class LookupService {

    private subjects: Map<string, CachedBehaviorSubject> = new Map<string, CachedBehaviorSubject>();

    constructor(private http: Http, @Inject(IErrorHandlerToken) private errorHandler: IErrorHandler) {
    }

    /**
     * Returns the promise that will resolves to the value of the lookup specified by the lookup tablename from the [Core] schema
     * @param {LookUpTableName} [lookupNameEnum] The table name of the lookup
     * @param {boolean} [includeExtra]
     * @param {(any) => any} [dataFormatter] A function that takes the api response as input and extracts the required data from it
     * @return {Promise<any>}
     */
    public getCoreLookup(lookupNameEnum: LookUpTableName, includeExtra: boolean = false, forceLoading: boolean = false, dataFormatter?: (any) => any): Promise<any> {
        return this.getLookup(lookupNameEnum, DBSchema.Core, forceLoading, includeExtra, dataFormatter);
    }

    public getLocalizationCoreLookup(lookupId: number, lookupNameEnum: LookUpTableName) {
        return this.GetLocalization(lookupId, lookupNameEnum, DBSchema.Core);
    }

    /**
     * Returns the promise that will resolves to the value of the lookup specified by the lookup tablename from the [dbo] schema
     * @param {LookUpTableName} [lookupNameEnum] The table name of the lookup
     * @param {boolean} [includeExtra]
     * @param {(any) => any} [dataFormatter] A function that takes the api response as input and extracts the required data from it
     * @return {Promise<any>}
     */
    public getdboLookup(lookupNameEnum: LookUpTableName, includeExtra: boolean = false, forceLoading: boolean = false, dataFormatter?: (any) => any): Promise<any> {
        return this.getLookup(lookupNameEnum, DBSchema.dbo, forceLoading, includeExtra, dataFormatter);
    }

    public getLocalizationDboLookup(lookupId: number, lookupNameEnum: LookUpTableName) {
        return this.GetLocalization(lookupId, lookupNameEnum, DBSchema.dbo);
    }

    /**
     * Returns the promise that will resolves to the value of the lookup specified by the lookup tablename and the db schema
     * @param {LookUpTableName} [lookupNameEnum] The table name of the lookup
     * @param {boolean} [includeExtra]
     * @param {DBSchema} [dbSchemaEnum] The database schema
     * @param {(any) => any} [dataFormatter] A function that takes the api response as input and extracts the required data from it
     * @return {Promise<any>}
     */
    public getLookup(lookupNameEnum: LookUpTableName, dbSchemaEnum: DBSchema, forceLoading: boolean, includeExtra: boolean = false, dataFormatter?: (any) => any): Promise<any> {
        let schema = DBSchema[dbSchemaEnum];
        let lookupName = LookUpTableName[lookupNameEnum];
        let id = this.getId(lookupNameEnum, dbSchemaEnum);

        let url = `${api.webApiEndpoint}${api.lookup_AllUrl}${lookupName}/${includeExtra}/${schema}`;
        return this.get(id, url, forceLoading, dataFormatter);
    }

    public GetLocalization(lookupId: number, lookupNameEnum: LookUpTableName, dbSchemaEnum: DBSchema) {
        let schema = DBSchema[dbSchemaEnum];
        let lookupName = LookUpTableName[lookupNameEnum];

        return this.http.get(`${api.webApiEndpoint}${api.lookup_AllUrl}${api.lookup_Localization}${lookupName}/${lookupId}/${schema}`)
            .map(res => res.json()).toPromise().then(res => {
                if (res && !res.IsErrorState) {
                    return res.Value;
                }
                else {
                    this.errorHandler.DefaultError();
                    return null;
                }
            });
    }

    /**
    * Returns the promise that will resolves to the value of the lookup specified by the lookup tablename from the [Core] schema
    * @param {LookUpTableName} [lookupNameEnum] The table name of the lookup
    * @param {boolean} [includeExtra]
    * @param {(any) => any} [dataFormatter] A function that takes the api response as input and extracts the required data from it
    * @return {Promise<any>}
    */
    public getCoreLongLookup(lookupNameEnum: LookUpTableName, includeExtra: boolean = false, forceLoading: boolean = false, dataFormatter?: (any) => any): Promise<any> {
        return this.getLongLookup(lookupNameEnum, DBSchema.Core, includeExtra, forceLoading, dataFormatter);
    }

    public getLocalizationCoreLongLookup(lookupId: number, lookupNameEnum: LookUpTableName) {
        return this.GetLongLocalization(lookupId, lookupNameEnum, DBSchema.Core);
    }

    /**
     * Returns the promise that will resolves to the value of the lookup specified by the lookup tablename and the db schema
     * @param {LookUpTableName} [lookupNameEnum] The table name of the lookup
     * @param {boolean} [includeExtra]
     * @param {DBSchema} [dbSchemaEnum] The database schema
     * @param {(any) => any} [dataFormatter] A function that takes the api response as input and extracts the required data from it
     * @return {Promise<any>}
     */
    public getLongLookup(lookupNameEnum: LookUpTableName, dbSchemaEnum: DBSchema, includeExtra: boolean = false, forceLoading: boolean = false, dataFormatter?: (any) => any): Promise<any> {
        let schema = DBSchema[dbSchemaEnum];
        let lookupName = LookUpTableName[lookupNameEnum];
        let id = this.getId(lookupNameEnum, dbSchemaEnum);

        let url = `${api.webApiEndpoint}${api.lookup_Long}${lookupName}/${includeExtra}/${schema}`;
        return this.get(id, url, forceLoading, dataFormatter);
    }

    public getLocalizationLongLookup(lookupId: number, lookupNameEnum: LookUpTableName) {
        return this.GetLongLocalization(lookupId, lookupNameEnum, DBSchema.dbo);
    }

    public GetLongLocalization(lookupId: number, lookupNameEnum: LookUpTableName, dbSchemaEnum: DBSchema) {
        let schema = DBSchema[dbSchemaEnum];
        let lookupName = LookUpTableName[lookupNameEnum];

        return this.http.get(`${api.webApiEndpoint}${api.lookup_Long}${api.lookup_Localization}${lookupName}/${lookupId}/${schema}`)
            .map(res => res.json()).toPromise().then(res => {
                if (res && !res.IsErrorState) {
                    return res.Value;
                }
                else {
                    this.errorHandler.DefaultError();
                    return null;
                }
            });
    }

    /**
 * Returns the promise that will resolves to the filtered value of the lookup specified by the lookup tablename from the [Core] schema
 * @param lookupNameEnum
 * @param filters
 * @param includeExtra
 * @param dataFormatter
 */
    public filterCoreLookup(lookupNameEnum: LookUpTableName, filters: FilterSearchCriteria[], includeExtra: boolean = false, dataFormatter?: (any) => any) {
        return this.filterLookup(lookupNameEnum, DBSchema.Core, filters, includeExtra, dataFormatter);
    }

    /**
     * Returns the promise that will resolves to the filtered value of the lookup specified by the lookup tablename from the [dbo] schema
     * @param lookupNameEnum
     * @param filters
     * @param includeExtra
     * @param dataFormatter
     */
    public filterdboLookup(lookupNameEnum: LookUpTableName, filters: FilterSearchCriteria[], includeExtra: boolean = false, dataFormatter?: (any) => any) {
        return this.filterLookup(lookupNameEnum, DBSchema.dbo, filters, includeExtra, dataFormatter);
    }

    /**
     * Returns the promise that will resolves to the filtered value of the lookup specified by the lookup tablename and the db schema
     * @param lookupNameEnum
     * @param dbSchemaEnum
     * @param filters
     * @param includeExtra
     * @param dataFormatter
     */
    public filterLookup(lookupNameEnum: LookUpTableName, dbSchemaEnum: DBSchema, filters: FilterSearchCriteria[], includeExtra: boolean = false, dataFormatter?: (any) => any) {
        let schema = DBSchema[dbSchemaEnum];
        let lookupName = LookUpTableName[lookupNameEnum];

        let url = `${api.webApiEndpoint}${api.lookup_AllUrl}${lookupName}/${includeExtra}/${schema}`;
        return this.post(url, filters, dataFormatter);
    }

    /**
     * Returns the promise that will resolves to the filtered value of the lookup specified by the lookup tablename from the [Core] schema
     * @param lookupNameEnum
     * @param filters
     * @param includeExtra
     * @param dataFormatter
     */
    public filterCoreLongLookup(lookupNameEnum: LookUpTableName, filters: FilterSearchCriteria[], includeExtra: boolean = false, dataFormatter?: (any) => any) {
        return this.filterLongLookup(lookupNameEnum, DBSchema.Core, filters, includeExtra, dataFormatter);
    }

    /**
     * Returns the promise that will resolves to the filtered value of the lookup specified by the lookup tablename from the [dbo] schema
     * @param lookupNameEnum
     * @param filters
     * @param includeExtra
     * @param dataFormatter
     */
    public filterdboLongLookup(lookupNameEnum: LookUpTableName, filters: FilterSearchCriteria[], includeExtra: boolean = false, dataFormatter?: (any) => any) {
        return this.filterLongLookup(lookupNameEnum, DBSchema.dbo, filters, includeExtra, dataFormatter);
    }

    /**
     * Returns the promise that will resolves to the filtered value of the lookup specified by the lookup tablename and the db schema
     * @param lookupNameEnum
     * @param dbSchemaEnum
     * @param filters
     * @param includeExtra
     * @param dataFormatter
     */
    public filterLongLookup(lookupNameEnum: LookUpTableName, dbSchemaEnum: DBSchema, filters: FilterSearchCriteria[], includeExtra: boolean = false, dataFormatter?: (any) => any) {
        let schema = DBSchema[dbSchemaEnum];
        let lookupName = LookUpTableName[lookupNameEnum];

        let url = `${api.webApiEndpoint}${api.lookup_Long}${lookupName}/${includeExtra}/${schema}`;
        return this.post(url, filters, dataFormatter);
    }

    /**
     * Returns the promise that will resolve to the value of lookup Id
     * @param lookupNameEnum The table name of the lookup
     * @param lookupWrite lookup object that will be saved in DB
     */
    public SaveCoreLongLookup(lookupNameEnum: LookUpTableName, lookupWrite: WriteLookup): Promise<any> {
        let lookupName = LookUpTableName[lookupNameEnum];

        let url = `${api.webApiEndpoint}${api.lookup_Long}${lookupName}`;

        return this.http.post(url, lookupWrite).toPromise();
    }

    /**
     * Returns the promise that will resolve to the value of deleting state 
     * @param lookupNameEnum The table name of the lookup
     * @param lookupId lookup element id that will be deleted from DB
     */
    public DeleteCoreLongLookup(lookupNameEnum: LookUpTableName, lookupId: number): Promise<any> {
        let lookupName = LookUpTableName[lookupNameEnum];

        let url = `${api.webApiEndpoint}${api.lookup_Long}${lookupName}/${lookupId}`;

        return this.http.delete(url).toPromise();
    }

    /**
     * Returns a promise that will resolves to the value returned by the specified url
     * @param {string} [url] The url containing the data
     * @param {(any) => any} [dataFormatter] A function that takes the api response as input and extracts the required data from it
     * @return {Promise<any>}
     */
    public getByUrl(url: string, dataFormatter?: (any) => any, forceLoading: boolean = false): Promise<any> {
        return this.get(url, url, forceLoading, dataFormatter);
    }

    /**
     * Forces the service to get the data from the http even if it hasn't expired yet.
     * This only applies if this lookup is cached.
     * @param {LookUpTableName} [lookupNameEnum] The table name of the lookup
     * @param {DBSchema} [dbSchemaEnum] The database schema
     * @return {void}
     */
    public invalidate(lookupNameEnum: LookUpTableName, dbSchemaEnum: DBSchema): void {
        let id = this.getId(lookupNameEnum, dbSchemaEnum);

        if (this.subjects.has(id)) {
            let cached = this.subjects.get(id);
            cached.force();
        }
    }

    /**
     * The main function for getting a promise that will resolve to the value of the lookup
     * @param {string} [id] The id of the lookup in the Map<,>
     * @param {string} [url] The url of the lookup
     * @param {(any) => any} [dataFormatter] A function that takes the api response as input and extracts the required data from it
     * @return {void}
     */
    private get(id: string, url: string, forceLoading: boolean, dataFormatter?: (any) => any): Promise<any> {
        dataFormatter = dataFormatter || ((data) => data.Value);

        if (this.subjects.has(id) && forceLoading) {
            this.subjects.delete(id);
        }

        if (!this.subjects.has(id)) {
            let subject = new CachedBehaviorSubject(url, dataFormatter, this.http, this.errorHandler);
            this.subjects.set(id, subject);
        }

        let cached = this.subjects.get(id);
        return cached.getPromise();
    }

    /**
     * The main function for getting a promise that will resolve to the filtring value of the lookup
     * @param url
     * @param filters
     * @param dataFormatter
     */
    private post(url: string, filters: FilterSearchCriteria[], dataFormatter?: (any) => any): Promise<any> {
        dataFormatter = dataFormatter || ((data) => data.Value);

        let successCallback = (result) => {

            // if IsErrorState
            if (!result || result.IsErrorState) {
                this.errorHandler.DefaultError();
            }
        };

        let errorCallback = (error) => {
            this.errorHandler.DefaultError();
        };

        // send an http request to get the data
        return this.http
            .post(url, filters)
            .map(res => res.json())
            .do(successCallback, errorCallback).map(res => dataFormatter(res)).toPromise();
    }

    private getId(lookupNameEnum: LookUpTableName, dbSchemaEnum: DBSchema): string {
        let schema = DBSchema[dbSchemaEnum];
        let lookupName = LookUpTableName[lookupNameEnum];

        return `${lookupName}##${schema}`;
    }
}

/**
 * @class Cacher
 * a private helper class for invalidating lookup data
 */
class CachedBehaviorSubject {

    private interestedSubjects: { resolve, reject }[] = [];
    private valueIsReady: boolean = false;
    private value: any = undefined;
    private lastFetchTime: any = new Date(1900, 1, 1);

    /**
     * Creates a new instance of the Cacher class
     * @param {string} [url] The lookup url
     * @param {(any) => any} [dataFormatter] A function that takes the api response as input and extracts the required data from it
     * @param {Http} [http] the http Service
     * @param {IErrorHandler} [errorHandler] The error handler that implements IErrorHandler interface
     * @param {number} [cachePeriod] The amount of time that should pass before considering the data obselete and making a new request.
     * @return {void}
     */
    constructor(
        private url: string,
        private dataFormatter: (any) => any,
        private http: Http,
        private errorHandler: IErrorHandler,
        private cachePeriod: number = 60 * 60   /*  1 Hour  */
    ) {
    }

    /**
     * checks to see if the caching period has elapsed or not, and executes the function if the period has passed.
     * the caching period has completed yet
     * @return {void}
     */
    private invalidate() {
        let now: any = new Date();
        let seconds = (now - this.lastFetchTime) / 1000;
        if (seconds > this.cachePeriod) {
            this.apiGet();
        }
    }

    public getPromise(): Promise<any> {
        this.invalidate();
        return new Promise((resolve, reject) => {
            if (this.valueIsReady) {
                resolve(this.value);
            } else {
                this.interestedSubjects.push({ resolve, reject });
            }
        });
    }

    /**
     * forces the cacher to make a call for the data immediately even if
     * the caching period has completed yet
     * @return {void}
     */
    public force() {
        this.apiGet();
    }

    /**
     * executes the function that the user specified passing the arguments (arguments are spread)
     * @return {void}
     */
    private apiGet() {
        //console.log('LookupService calling : ' + this.url);
        this.value = undefined;
        this.valueIsReady = false;
        this.lastFetchTime = new Date();

        let successCallback = (result) => {

            // if IsErrorState
            if (!result || result.IsErrorState) {
                return this.errorHandler.DefaultError();
            }

            // Success, Data is available
            this.value = this.dataFormatter(result);
            this.valueIsReady = true;
            this.doWithInterestedSubjects(pr => pr.resolve(this.value));
        };

        let errorCallback = (error) => {
            this.errorHandler.DefaultError();
            this.doWithInterestedSubjects(pr => pr.reject(error));
        };

        // send an http request to get the data
        this.http
            .get(this.url)
            .map(res => res.json())
            .subscribe(successCallback, errorCallback);
    }

    private doWithInterestedSubjects(fun: any) {
        this.interestedSubjects.forEach(fun);
        this.interestedSubjects.length = 0;
    }

}


// WEBPACK FOOTER //
// D:/TFS/Softec.FMS-Core-Live/Source/Frontend/Web/Softec.FMS.Client.WebView-ng4/app/shared/services/lookup.service.ts